// Search for the quick find in the header: matches pages and help articles on their title,
// section, description and (for help articles) their text. Runs in the browser, as all of
// it is in the translations already.

export type QuickFindKind = 'page' | 'help';

export type QuickFindEntry = {
  kind: QuickFindKind;
  path: string;
  title: string;
  /** Shown next to the title, e.g. the menu section of a page */
  context?: string;
  description: string;
  /** Full text that is searched but not shown, e.g. the text of a help article */
  body?: string;
};

/** Lower case without accents, so "cafe" also finds "café". Æ, ø and å are letters of their own and stay. */
export const normalizeText = (text: string): string =>
  text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const startsWord = (text: string, word: string): boolean =>
  text.startsWith(word) || text.includes(` ${word}`) || text.includes(`-${word}`);

/**
 * Score of an entry for the query; 0 when not every word of the query matches.
 * A word in the title counts most, then in the section or description, then in the text.
 */
const scoreEntry = (entry: QuickFindEntry, words: string[], query: string): number => {
  const title = normalizeText(entry.title);
  const meta = normalizeText(`${entry.context ?? ''} ${entry.description}`);
  const body = normalizeText(entry.body ?? '');

  let score = 0;
  for (const word of words) {
    if (title.includes(word)) score += startsWord(title, word) ? 10 : 6;
    else if (meta.includes(word)) score += startsWord(meta, word) ? 4 : 3;
    else if (body.includes(word)) score += 1;
    else return 0;
  }
  if (title.startsWith(query)) score += 5;
  return score;
};

/** Entries matching every word of the query, best first. An empty query returns all entries. */
export const searchQuickFind = (entries: QuickFindEntry[], query: string): QuickFindEntry[] => {
  const normalized = normalizeText(query.trim());
  if (!normalized) return entries;
  const words = normalized.split(/\s+/);

  return entries
    .map((entry, index) => ({ entry, index, score: scoreEntry(entry, words, normalized) }))
    .filter(({ score }) => score > 0)
    // Equal scores keep their order, so pages stay before help articles
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ entry }) => entry);
};

/** All text in a nested translation object, e.g. the paragraphs and lists of a help article. */
export const collectText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(collectText).join(' ');
  if (value && typeof value === 'object') return Object.values(value).map(collectText).join(' ');
  return '';
};
